import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Loader2, Sparkles, User, ChevronDown, ChevronUp, ArrowRight, Mic, MicOff, Clock, Heart } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { useWebSpeechRecording } from '@/hooks/useWebSpeechRecording';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigatorChat } from '@/components/dashboard/client/ai-navigator/useNavigatorChat';
import { saveRedirectPath } from '@/utils/auth-redirect';
import { useFastNavigation } from '@/hooks/useFastNavigation';
import { trackAIEvent, trackAIEventBeacon, classifyInputLength, classifyProblemType, classifyAIResponse } from '@/utils/aiTrace';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { getGuestNavigatorSessionId } from '@/components/dashboard/client/ai-navigator/navigatorConversationStorage';
import { setDeepModeContext, consumeDeepModeResult } from '@/components/dashboard/client/ai-navigator/navigatorDeepContext';
import SessionResumePrompt from '@/components/dashboard/client/ai-navigator/SessionResumePrompt';
import { detectCrisis, forceCrisisRedirect } from '@/utils/crisisDetection';
import { toast } from 'sonner';

interface WellnessConciergeProps {
  onStageRecommended?: (stage: number) => void;
}

const CRISIS_HELP_URL = 'https://resonow.org/crisis-resources';

const STAGE_NAMES: Record<number, { en: string; zh: string }> = {
  0: { en: 'Crisis Intervention', zh: '危机干预' },
  1: { en: 'Harmonize Mind & Body', zh: '身心调和' },
  2: { en: 'Analysis & Investigation', zh: '分析与探究' },
  3: { en: 'Transformation & Growth', zh: '转化与成长' },
  4: { en: 'Integration & Mastery', zh: '整合与精通' },
};

// ── Concern category chips ──
const CONCERN_CHIPS_EN = [
  { label: 'Anxiety', value: 'anxiety' },
  { label: 'Relationship', value: 'relationship difficulties' },
  { label: 'Sleep', value: 'sleep problems' },
  { label: 'Burnout', value: 'burnout and exhaustion' },
  { label: 'Feeling lost', value: 'feeling lost' },
];

const CONCERN_CHIPS_ZH = [
  { label: '焦虑', value: '焦虑' },
  { label: '关系', value: '关系困扰' },
  { label: '睡眠', value: '睡眠问题' },
  { label: '倦怠', value: '倦怠和疲惫' },
  { label: '迷茫', value: '感到迷茫' },
];

// ── Duration chips ──
const DURATION_CHIPS_EN = [
  { label: 'Just now', value: 'just started recently' },
  { label: 'Weeks', value: 'for several weeks' },
  { label: 'Months', value: 'for months' },
  { label: 'Long-term', value: 'for a long time, possibly years' },
];

const DURATION_CHIPS_ZH = [
  { label: '刚开始', value: '最近刚开始' },
  { label: '几周了', value: '持续了几周' },
  { label: '几个月', value: '已经几个月了' },
  { label: '长期', value: '长期存在，可能好几年了' },
];

// ── Dynamic follow-up chip pools ──
// Chips must align with the conversation stage and the AI's most recent
// response_type. They should *continue* the dialogue, not jump ahead to
// recommendations before the user has shared enough context.
type ChipStage = 'early' | 'mid' | 'late' | 'conversion';

const DYNAMIC_CHIPS: Record<ChipStage, { en: string[]; zh: string[] }> = {
  // Input is too short / vague — invite the user to express more.
  early: {
    en: [
      'Tell me a bit more',
      'What’s been hardest?',
      'When did this start?',
      'How does it feel in your body?',
    ],
    zh: [
      '可以再多说一点吗？',
      '最让你难受的是什么？',
      '这种感觉是什么时候开始的？',
      '身体上有什么反应？',
    ],
  },
  // User has shared something — help them add structure / specifics.
  // Includes "Go deeper" once enough context exists for the consciousness engine.
  mid: {
    en: [
      'Add more context',
      'How often does it happen?',
      'What have you already tried?',
      'Go deeper with analysis',
    ],
    zh: [
      '补充更多背景',
      '它出现得有多频繁？',
      '你之前尝试过什么方法？',
      '深入分析',
    ],
  },
  // Enough context — invite analysis / pathway exploration.
  late: {
    en: [
      'Go deeper with analysis',
      'What might help?',
      'Show me options',
      'Help me understand why',
    ],
    zh: [
      '深入分析',
      '有哪些可能有帮助的方式？',
      '给我看看选项',
      '帮我理解原因',
    ],
  },
  // AI has produced a recommendation — drive conversion, but still offer
  // the deep-analysis path for users who want the full picture first.
  conversion: {
    en: [
      'View practitioners',
      'Book a session',
      'Go deeper with analysis',
      'Save for later',
    ],
    zh: [
      '查看疗愈师',
      '预约一次咨询',
      '深入分析',
      '稍后再看',
    ],
  },
};

const SERVICE_INTENT_PATTERN = /\b(who can help|find (a )?(therapist|practitioner|healer)|book|booking|appointment|recommend(ed)? services?|see services?|service recommendation)\b|找(疗愈师|咨询师|服务)|预约|预订|推荐(服务|疗愈师|咨询师)|谁能帮/i;

/**
 * Parse [quick_reply: opt1 | opt2 | opt3] from AI response.
 * Returns { cleanContent, quickReplies } where cleanContent has the tag stripped.
 */
function parseQuickReplies(content: string): { cleanContent: string; quickReplies: string[] } {
  const match = content.match(/\[quick_reply:\s*(.+?)\]/);
  if (!match) return { cleanContent: content, quickReplies: [] };
  const options = match[1].split('|').map(o => o.trim()).filter(Boolean);
  const cleanContent = content.replace(/\[quick_reply:\s*.+?\]/g, '').trim();
  return { cleanContent, quickReplies: options };
}

/**
 * Build a structured prompt from guided selections + optional free text.
 */
function buildStructuredInput(
  concern: string | null,
  duration: string | null,
  freeText: string,
  isZh: boolean,
): string {
  const parts: string[] = [];

  if (concern) {
    parts.push(isZh ? `我的主要困扰是：${concern}` : `My main concern: ${concern}`);
  }
  if (duration) {
    parts.push(isZh ? `持续时间：${duration}` : `Duration: ${duration}`);
  }
  if (freeText.trim()) {
    parts.push(isZh ? `补充描述：${freeText.trim()}` : `Additional details: ${freeText.trim()}`);
  }

  if (!concern && !duration) return freeText.trim();

  return parts.join('\n');
}

const WellnessConcierge: React.FC<WellnessConciergeProps> = ({ onStageRecommended }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { fastNavigate, prefetch, pendingPath } = useFastNavigation();
  const { user } = useAuth();
  
  const isZh = i18n.language?.startsWith('zh');
  const [inputValue, setInputValue] = useState('');
  const {
    messages,
    isLoading,
    recommendations,
    sendMessage: sendNavigatorMessage,
    uiState,
    setUiState,
    phase,
    appendAssistantMessage,
    pendingGuestResume,
    resumeGuestConversation,
    dismissGuestResume,
  } = useNavigatorChat();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCrisisState, setIsCrisisState] = useState(false);
  const [showInlineRecommendations, setShowInlineRecommendations] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const quickReplyOptions = uiState.quickReplies || [];
  const detectedStage = uiState.detectedStage ?? null;
  const userTurnCount = messages.filter((message) => message.role === 'user').length;
  const latestUserMessage = [...messages].reverse().find((message) => message.role === 'user')?.content || '';
  const hasServiceIntent = SERVICE_INTENT_PATTERN.test(latestUserMessage);
  const shouldShowBranchCard = !isLoading && !isCrisisState && userTurnCount >= 3 && userTurnCount <= 5 && !uiState.branchChoice && !hasServiceIntent;
  const shouldShowServicePanel = !isLoading && recommendations.length > 0 && (showInlineRecommendations || hasServiceIntent || uiState.branchChoice === 'services');
  const branchShownRef = useRef(false);
  const recoShownRef = useRef(false);
  const sessionStartedRef = useRef(false);

  const guestSessionId = !user?.id ? getGuestNavigatorSessionId() : null;

  useEffect(() => {
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    trackAIEvent({
      event_type: 'session_start',
      guest_session_id: guestSessionId,
      context: 'homepage',
    } as any);
  }, [guestSessionId, user?.id]);

  useEffect(() => {
    if (shouldShowBranchCard && !branchShownRef.current) {
      branchShownRef.current = true;
      trackAIEvent({
        event_type: 'branch_card_shown',
        turn_index: userTurnCount,
        guest_session_id: guestSessionId,
        problem_type: classifyProblemType(latestUserMessage),
        context: 'homepage',
      });
    }
  }, [shouldShowBranchCard, userTurnCount, guestSessionId, latestUserMessage]);

  useEffect(() => {
    if (shouldShowServicePanel && !recoShownRef.current) {
      recoShownRef.current = true;
      trackAIEvent({
        event_type: 'recommendation_shown',
        turn_index: userTurnCount,
        guest_session_id: guestSessionId,
        recommended_service: true,
        stage: recommendations[0]?.chata_stage ?? null,
        problem_type: classifyProblemType(latestUserMessage),
        context: 'homepage',
      });
    }
  }, [shouldShowServicePanel, userTurnCount, guestSessionId, recommendations, latestUserMessage]);

  // Classify each completed AI assistant reply (structured, no full text stored)
  const lastClassifiedAssistantRef = useRef<string>('');
  useEffect(() => {
    if (isLoading) return;
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant) return;
    const fingerprint = `${messages.length}:${lastAssistant.content.length}`;
    if (lastClassifiedAssistantRef.current === fingerprint) return;
    lastClassifiedAssistantRef.current = fingerprint;
    const cls = classifyAIResponse(lastAssistant.content);
    trackAIEvent({
      event_type: 'ai_response',
      turn_index: userTurnCount,
      response_length: cls.length,
      contains_question: cls.containsQuestion,
      contains_recommendation: cls.containsRecommendation,
      response_type: cls.type,
      guest_session_id: guestSessionId,
      context: 'homepage',
      stage: typeof phase === 'number' ? phase : null,
    });
  }, [isLoading, messages, userTurnCount, guestSessionId, phase]);

  // Drop-off detection: when the user closes the tab mid-conversation,
  // capture the last turn + stage as exit point via sendBeacon.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      if (userTurnCount === 0) return;
      // Only fire if user hasn't clicked a clear conversion path
      const branched = !!uiState.branchChoice;
      if (branched) return;
      trackAIEventBeacon(
        {
          event_type: 'drop_off',
          guest_session_id: guestSessionId,
          context: 'homepage',
          exit_turn: userTurnCount,
          exit_stage: typeof phase === 'number' ? phase : null,
          turn_index: userTurnCount,
        },
        SUPABASE_URL,
        SUPABASE_PUBLISHABLE_KEY,
      );
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, [userTurnCount, guestSessionId, uiState.branchChoice, phase]);

  // Guided input state
  const [selectedConcern, setSelectedConcern] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [guidedStep, setGuidedStep] = useState<'concern' | 'duration' | 'details'>('concern');

  const redirectToCrisisResources = useCallback(() => {
    forceCrisisRedirect({ delayMs: 600 });
  }, []);

  // Server-side crisis signal (phase === 'crisis' from AI metadata)
  useEffect(() => {
    if (phase === 'crisis' && !isCrisisState) {
      setIsCrisisState(true);
      trackAIEvent({
        event_type: 'crisis_detected',
        context: 'homepage',
        guest_session_id: guestSessionId,
        turn_index: userTurnCount,
      });
      toast.error(
        isZh ? '正在为您跳转到危机支持资源…' : 'Redirecting you to support resources…',
        { duration: 1500 },
      );
      forceCrisisRedirect({ delayMs: 900 });
    }
  }, [phase, isCrisisState, isZh, guestSessionId, userTurnCount]);

  const {
    isRecording,
    transcript,
    interimTranscript,
    isSupported: speechSupported,
    startRecording,
    stopRecording,
    resetTranscript,
  } = useWebSpeechRecording({ language: '', continuous: false, interimResults: true });

  useEffect(() => {
    if (transcript) {
      setInputValue(prev => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  const concernChips = isZh ? CONCERN_CHIPS_ZH : CONCERN_CHIPS_EN;
  const durationChips = isZh ? DURATION_CHIPS_ZH : DURATION_CHIPS_EN;

  // ── Dynamic suggestion chips ──
  // Pick a chip stage that mirrors the AI's current posture so the chips feel
  // like a natural continuation of the dialogue rather than a fixed menu.
  const followUpSuggestions = useMemo<string[]>(() => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const userInputStats = lastUser ? classifyInputLength(lastUser.content) : null;
    const aiCls = lastAssistant ? classifyAIResponse(lastAssistant.content) : null;

    // Conversion stage — recommendations are visible OR AI explicitly
    // surfaced a recommendation OR the user expressed service intent.
    const inConversion =
      recommendations.length > 0 ||
      hasServiceIntent ||
      aiCls?.containsRecommendation === true ||
      uiState.branchChoice === 'services';

    let chipStage: ChipStage;
    if (inConversion) {
      chipStage = 'conversion';
    } else if (userTurnCount === 0) {
      // No user turn yet — fall back to early prompts (matches empty-state intent).
      chipStage = 'early';
    } else if (
      userTurnCount <= 1 ||
      userInputStats?.bucket === 'short' ||
      aiCls?.type === 'clarification'
    ) {
      // AI is still asking clarifying questions or input is very brief.
      chipStage = 'early';
    } else if (userTurnCount <= 3 || aiCls?.type === 'other') {
      chipStage = 'mid';
    } else {
      chipStage = 'late';
    }

    const pool = DYNAMIC_CHIPS[chipStage];
    return isZh ? pool.zh : pool.en;
  }, [messages, recommendations.length, hasServiceIntent, uiState.branchChoice, userTurnCount, isZh]);

  useEffect(() => {
    prefetch('/client-auth');
    prefetch('/signup');
    prefetch('/client-dashboard?tab=ai-navigator');
    prefetch('/booking-choice');
    prefetch('/book-an-appointment');
    prefetch('/framework');
  }, [prefetch]);

  // ── Deep Mode handoff ──
  // Push the current conversation into the bridge and navigate to /framework
  // where the consciousness engine auto-runs on the SAME content. The user
  // never re-types anything — same AI, going deeper.
  const handleGoDeeper = useCallback(() => {
    if (messages.length === 0) {
      toast.message(isZh ? '先和 Reso 聊一会儿吧' : 'Share a few thoughts with Reso first');
      return;
    }
    setDeepModeContext({
      messages,
      detectedStage: uiState.detectedStage ?? null,
      guestSessionId,
      handoffAt: new Date().toISOString(),
      origin: 'homepage',
    });
    trackAIEvent({
      event_type: 'recommendation_click',
      event_label: 'deep_mode_handoff',
      target_path: '/framework',
      turn_index: userTurnCount,
      stage: typeof phase === 'number' ? phase : null,
      guest_session_id: guestSessionId,
      context: 'homepage',
    } as any);
    fastNavigate('/framework');
  }, [messages, uiState.detectedStage, guestSessionId, userTurnCount, phase, fastNavigate, isZh]);

  // ── Deep Mode return ──
  // After /framework completes deep analysis, we get a structured summary
  // back via sessionStorage. Append it as an assistant turn so the chat
  // narrative stays unified — no separate "second AI" output.
  const deepResultConsumedRef = useRef(false);
  useEffect(() => {
    if (deepResultConsumedRef.current) return;
    if (messages.length === 0) return; // wait until existing conversation is loaded
    const result = consumeDeepModeResult();
    if (!result) return;
    deepResultConsumedRef.current = true;
    appendAssistantMessage(result.summary);
  }, [messages.length, appendAssistantMessage]);

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStageClick = useCallback((stageNum: number) => {
    setUiState({ ...uiState, detectedStage: stageNum, clickedServiceLinks: [...new Set([...(uiState.clickedServiceLinks || []), `stage:${stageNum}`])] });
    trackAIEvent({
      event_type: 'stage_click',
      stage: stageNum,
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      context: 'homepage',
    });
    if (onStageRecommended) {
      onStageRecommended(stageNum);
    }
  }, [onStageRecommended, setUiState, uiState, userTurnCount, guestSessionId]);

  const handleMicToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const markdownComponents = useMemo(() => ({
    strong: ({ children, ...props }: any) => {
      const text = String(children);
      const stageMatch = text.match(/Recommended\s*Stage:\s*Stage\s*(\d)/i) 
        || text.match(/推荐阶段[：:]\s*Stage\s*(\d)/i)
        || text.match(/推荐阶段[：:]\s*第(\d)阶段/i);
      
      if (stageMatch) {
        const stageNum = parseInt(stageMatch[1], 10);
        if (stageNum >= 0 && stageNum <= 4) {
          const stageName = isZh ? STAGE_NAMES[stageNum]?.zh : STAGE_NAMES[stageNum]?.en;
          return (
            <button
              onClick={() => handleStageClick(stageNum)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors cursor-pointer border-0"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {isZh ? `进入 Stage ${stageNum} — ${stageName}` : `Go to Stage ${stageNum} — ${stageName}`}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          );
        }
      }
      return <strong {...props}>{children}</strong>;
    },
    a: ({ href, children, ...props }: any) => {
      if (href && href.startsWith('/book-an-appointment/')) {
        return (
          <button
            onClick={() => {
              setUiState({ ...uiState, clickedServiceLinks: [...new Set([...(uiState.clickedServiceLinks || []), href])] });
              navigate(href);
            }}
            className="inline-flex items-center gap-1 text-brand-600 font-medium hover:text-brand-700 underline underline-offset-2 cursor-pointer border-0 bg-transparent p-0"
          >
            {children}
            <ArrowRight className="h-3 w-3" />
          </button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
  }), [isZh, handleStageClick, navigate, setUiState, uiState]);

  const sendMessage = useCallback(async (input: string) => {
    if (!input.trim() || isLoading) return;
    if (isCrisisState) {
      redirectToCrisisResources();
      return;
    }
    // P0: Crisis keyword detection — force redirect, bypass all AI / recommendation flow
    if (detectCrisis(input)) {
      setIsCrisisState(true);
      trackAIEvent({
        event_type: 'crisis_detected',
        context: 'homepage',
        guest_session_id: guestSessionId,
        turn_index: userTurnCount + 1,
        input_text: input,
      });
      toast.error(
        isZh ? '正在为您跳转到危机支持资源…' : 'Redirecting you to support resources…',
        { duration: 1500 },
      );
      forceCrisisRedirect({ delayMs: 900 });
      return;
    }
    setIsExpanded(true);
    const stats = classifyInputLength(input);
    trackAIEvent({
      event_type: 'user_message',
      turn_index: userTurnCount + 1,
      input_text: input,
      input_word_count: stats.words,
      input_char_count: stats.chars,
      input_length_bucket: stats.bucket,
      used_chip: !!selectedConcern,
      used_structured_input: !!(selectedConcern || selectedDuration),
      added_details: !!inputValue.trim() && (!!selectedConcern || !!selectedDuration),
      problem_type: classifyProblemType(input),
      guest_session_id: guestSessionId,
      context: 'homepage',
    });
    setUiState({ ...uiState, quickReplies: [], detectedStage: null });
    await sendNavigatorMessage(input, i18n.language);
  }, [isLoading, isCrisisState, redirectToCrisisResources, setUiState, uiState, sendNavigatorMessage, i18n.language, userTurnCount, selectedConcern, selectedDuration, inputValue, guestSessionId]);

  // Handle sending with structured input enrichment
  const handleSend = useCallback(() => {
    const structured = buildStructuredInput(selectedConcern, selectedDuration, inputValue, !!isZh);
    if (!structured.trim()) return;
    sendMessage(structured);
    setInputValue('');
    setSelectedConcern(null);
    setSelectedDuration(null);
    setGuidedStep('concern');
  }, [inputValue, selectedConcern, selectedDuration, isZh, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConcernSelect = useCallback((value: string) => {
    if (selectedConcern === value) {
      setSelectedConcern(null);
      setGuidedStep('concern');
    } else {
      setSelectedConcern(value);
      setGuidedStep('duration');
    }
  }, [selectedConcern]);

  const handleDurationSelect = useCallback((value: string) => {
    if (selectedDuration === value) {
      setSelectedDuration(null);
    } else {
      setSelectedDuration(value);
      setGuidedStep('details');
    }
  }, [selectedDuration]);

  const handleQuickSend = useCallback(() => {
    if (!selectedConcern) return;
    const structured = buildStructuredInput(selectedConcern, selectedDuration, inputValue, !!isZh);
    sendMessage(structured);
    setInputValue('');
    setSelectedConcern(null);
    setSelectedDuration(null);
    setGuidedStep('concern');
  }, [selectedConcern, selectedDuration, inputValue, isZh, sendMessage]);

  // Handle quick reply button click
  const handleQuickReply = useCallback((reply: string) => {
    setUiState({ ...uiState, quickReplies: [] });
    sendMessage(reply);
  }, [sendMessage, setUiState, uiState]);

  const continueInDashboard = useCallback(() => {
    const nextState = { ...uiState, branchChoice: 'continue' as const, branchPromptShownAt: new Date().toISOString() };
    setUiState(nextState);
    trackAIEvent({
      event_type: 'branch_choice',
      branch_choice: 'continue',
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      target_path: user ? '/client-dashboard?tab=ai-navigator' : '/client-auth',
      context: 'homepage',
    });
    if (!user) {
      trackAIEvent({
        event_type: 'login_redirect',
        target_path: '/client-auth',
        guest_session_id: guestSessionId,
        context: 'homepage',
      });
    }
    if (user) {
      fastNavigate('/client-dashboard?tab=ai-navigator');
      return;
    }
    saveRedirectPath('/client-dashboard?tab=ai-navigator');
    fastNavigate('/client-auth');
  }, [fastNavigate, setUiState, uiState, user, userTurnCount, guestSessionId]);

  const viewRecommendedServices = useCallback(() => {
    setUiState({
      ...uiState,
      branchChoice: 'services',
      branchPromptShownAt: new Date().toISOString(),
      clickedServiceLinks: [...new Set([...(uiState.clickedServiceLinks || []), 'homepage:recommended-services'])],
    });
    trackAIEvent({
      event_type: 'branch_choice',
      branch_choice: 'services',
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      stage: recommendations[0]?.chata_stage ?? null,
      context: 'homepage',
    });
    setShowInlineRecommendations(true);
  }, [setUiState, uiState, userTurnCount, guestSessionId, recommendations]);

  const saveAndContinueLater = useCallback(() => {
    setUiState({ ...uiState, branchChoice: 'save_later', branchPromptShownAt: new Date().toISOString() });
    trackAIEvent({
      event_type: 'branch_choice',
      branch_choice: 'save_later',
      turn_index: userTurnCount,
      guest_session_id: guestSessionId,
      target_path: user ? '/client-dashboard?tab=ai-navigator' : '/signup',
      context: 'homepage',
    });
    if (!user) {
      trackAIEvent({
        event_type: 'signup_redirect',
        target_path: '/signup',
        guest_session_id: guestSessionId,
        context: 'homepage',
      });
    }
    saveRedirectPath('/client-dashboard?tab=ai-navigator');
    fastNavigate(user ? '/client-dashboard?tab=ai-navigator' : '/signup');
  }, [fastNavigate, setUiState, uiState, user, userTurnCount, guestSessionId]);

  if (isCrisisState) {
    return (
      <div className="max-w-2xl mx-auto mb-10">
        <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 text-center space-y-4">
            <h2 className="text-xl md:text-2xl font-serif text-foreground">
              {isZh ? '请立即寻求帮助' : 'Please Get Immediate Help'}
            </h2>
            <Button asChild size="lg" className="bg-brand-500 hover:bg-brand-600 text-white rounded-full">
              <a href={CRISIS_HELP_URL} target="_blank" rel="noopener noreferrer">
                {isZh ? '获取即时帮助' : 'Get Immediate Help'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasGuidedSelection = selectedConcern || selectedDuration;
  const canSendGuided = !!selectedConcern;
  const showGuidedInput = messages.length === 0 && !isCrisisState;

  return (
    <div className="max-w-2xl mx-auto mb-10">
      <div className="text-center mb-4">
        <h2 className="text-2xl md:text-3xl font-serif text-foreground">
          {isZh ? '你的' : 'Your Personal '}
          <span className="text-brand-500 italic">{isZh ? '专属疗愈顾问' : 'Wellness Concierge'}</span>
        </h2>
        {user && messages.length > 0 && (
          <Button
            variant="link"
            className="mt-1 h-auto p-0 text-brand-600"
            onClick={() => navigate('/client-dashboard?tab=ai-navigator')}
          >
            {isZh ? '在 Dashboard 继续上次对话' : 'Continue this conversation in Dashboard'}
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
        {pendingGuestResume && (
          <div className="px-4 pt-4">
            <SessionResumePrompt
              userTurns={pendingGuestResume.userTurns}
              lastUpdatedAt={pendingGuestResume.lastUpdatedAt}
              reachedBranch={pendingGuestResume.reachedBranch}
              onContinue={resumeGuestConversation}
              onStartFresh={dismissGuestResume}
            />
          </div>
        )}
        {/* Chat messages area */}
        {isExpanded && messages.length > 0 && (
          <ScrollArea ref={scrollAreaRef} className="h-[400px]">
            <div className="divide-y divide-warm-100">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'flex gap-3 px-4 py-3',
                    msg.role === 'user' ? 'flex-row-reverse' : '',
                    msg.role === 'assistant' ? 'bg-warm-50/50' : ''
                  )}
                >
                  <div className={cn(
                    'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5',
                    msg.role === 'user' ? 'bg-brand-100 text-brand-700' : 'bg-brand-50 text-brand-500'
                  )}>
                    {msg.role === 'user' ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                  </div>
                  <div className={cn(
                    'min-w-0 overflow-hidden max-w-[85%]',
                    msg.role === 'user' ? 'flex flex-col items-end' : ''
                  )}>
                    {msg.role === 'user' ? (
                      <div className="bg-brand-100 rounded-2xl rounded-tr-sm px-4 py-2.5">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:my-2 prose-strong:text-brand-700">
                        <ReactMarkdown components={markdownComponents}>{msg.content}</ReactMarkdown>
                        {isLoading && idx === messages.length - 1 && (
                          <span className="inline-block w-1.5 h-4 bg-brand-400 animate-pulse ml-0.5 align-text-bottom" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick reply buttons — shown after AI asks a follow-up question */}
              {!isLoading && quickReplyOptions.length > 0 && (
                <div className="px-4 py-3 bg-brand-50/20">
                  <div className="flex flex-wrap gap-1.5">
                    {quickReplyOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleQuickReply(option)}
                        className="px-3 py-1.5 text-xs rounded-full border border-brand-300 bg-white text-brand-700 hover:bg-brand-100 hover:border-brand-400 transition-colors"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Natural branch point after a few turns — choices instead of forced recommendation */}
              {shouldShowBranchCard && (
                <div className="px-4 py-3 bg-brand-50/20">
                  <div className="rounded-lg border border-brand-200 bg-background p-3 space-y-3">
                    <p className="text-sm text-foreground">
                      {isZh
                        ? '我已经更清楚地理解了一些正在发生的事。接下来你想怎么走？'
                        : 'I’ve got a clearer sense of what’s going on. What would you like to do next?'}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <Button variant="outline" size="sm" className="h-auto whitespace-normal py-2" onClick={continueInDashboard}>
                        {isZh ? '继续探索' : 'Continue exploring'}
                      </Button>
                      <Button variant="outline" size="sm" className="h-auto whitespace-normal py-2" onClick={viewRecommendedServices}>
                        {isZh ? '查看推荐疗愈师' : 'See recommended practitioners'}
                      </Button>
                      <Button variant="outline" size="sm" className="h-auto whitespace-normal py-2" onClick={saveAndContinueLater}>
                        {isZh ? '保存稍后继续' : 'Save & continue later'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Optional recommendations — shown by choice or explicit service intent */}
              {shouldShowServicePanel && (
                <div className="px-4 py-3 bg-brand-50/20">
                  <div className="rounded-lg border border-brand-200 bg-background p-3 space-y-3">
                    <p className="text-sm font-medium text-foreground">
                      {isZh ? '基于当前对话的支持路径' : 'Support path from this conversation'}
                    </p>
                    {recommendations[0] && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div className="rounded-md border border-border bg-muted/30 p-2">
                          <p className="text-xs text-muted-foreground">{isZh ? 'Primary Stage' : 'Primary Stage'}</p>
                          <p className="text-sm font-medium text-foreground">Stage {recommendations[0].chata_stage}</p>
                        </div>
                        <div className="rounded-md border border-border bg-muted/30 p-2">
                          <p className="text-xs text-muted-foreground">{isZh ? 'Supportive Stage' : 'Supportive Stage'}</p>
                          <p className="text-sm font-medium text-foreground">Stage {Math.max(0, Math.min(4, recommendations[0].chata_stage + 1))}</p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      {recommendations.slice(0, 3).map((rec, index) => (
                        <div key={`${rec.name_en}-${index}`} className="rounded-md border border-border bg-muted/30 p-2">
                          <p className="text-sm font-medium text-foreground">{isZh ? rec.name_zh : rec.name_en}</p>
                          <p className="text-xs text-muted-foreground mt-1">{rec.reason}</p>
                          {rec.matched_practitioners?.slice(0, 2).map((practitioner) => (
                            <div key={practitioner.slug} className="mt-2 flex items-center justify-between gap-2 rounded border border-border bg-background p-2">
                              <span className="min-w-0 truncate text-xs font-medium text-foreground">{practitioner.name}</span>
                              <div className="flex shrink-0 gap-1">
                                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onMouseEnter={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onFocus={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onClick={() => { trackAIEvent({ event_type: 'recommendation_click', clicked_recommendation: true, target_path: `/book-an-appointment/${practitioner.slug}`, event_label: 'view_profile', stage: rec.chata_stage, guest_session_id: guestSessionId, context: 'homepage' }); fastNavigate(`/book-an-appointment/${practitioner.slug}`); }} disabled={pendingPath === `/book-an-appointment/${practitioner.slug}`}>{pendingPath === `/book-an-appointment/${practitioner.slug}` ? (isZh ? '加载中' : 'Loading') : (isZh ? '主页' : 'View profile')}</Button>
                                <Button size="sm" className="h-7 px-2 text-xs bg-brand-500 hover:bg-brand-600 text-white" onMouseEnter={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onFocus={() => prefetch(`/book-an-appointment/${practitioner.slug}`)} onClick={() => { trackAIEvent({ event_type: 'recommendation_click', clicked_recommendation: true, target_path: `/book-an-appointment/${practitioner.slug}`, event_label: 'book', stage: rec.chata_stage, guest_session_id: guestSessionId, context: 'homepage' }); fastNavigate(`/book-an-appointment/${practitioner.slug}`); }} disabled={pendingPath === `/book-an-appointment/${practitioner.slug}`}>{isZh ? '预约' : 'Book'}</Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    <Button size="sm" className="w-full bg-brand-500 hover:bg-brand-600 text-white" onMouseEnter={() => prefetch('/booking-choice')} onFocus={() => prefetch('/booking-choice')} onClick={() => fastNavigate('/booking-choice')} disabled={pendingPath === '/booking-choice'}>
                      {isZh ? '查看可预约服务' : 'View bookable services'}
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Stage navigation button */}
               {!isLoading && !isCrisisState && detectedStage !== null && (
                <div className="px-4 py-3 bg-brand-50/30">
                  <button
                    onClick={() => handleStageClick(detectedStage)}
                    className="w-full text-center text-sm font-medium text-brand-700 bg-brand-100 hover:bg-brand-200 border border-brand-300 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {isZh
                      ? `查看 Stage ${detectedStage} 的所有疗愈师`
                      : `View All Stage ${detectedStage} Practitioners`}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        )}

        {/* Guided input section — only before first message */}
        {showGuidedInput && (
          <div className="px-4 pt-4 pb-2 space-y-3">
            {/* Step 1: Concern chips */}
            <div>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                <Heart className="h-3 w-3" />
                {isZh ? '最近让你困扰的是？' : "What's been on your mind?"}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {concernChips.map((chip) => (
                  <button
                    key={chip.value}
                    onClick={() => handleConcernSelect(chip.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-full border transition-all duration-200',
                      selectedConcern === chip.value
                        ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                        : 'bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50'
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Duration chips — shown after concern selection */}
            {selectedConcern && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  {isZh ? '持续了多久？' : 'How long has this been going on?'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {durationChips.map((chip) => (
                    <button
                      key={chip.value}
                      onClick={() => handleDurationSelect(chip.value)}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-full border transition-all duration-200',
                        selectedDuration === chip.value
                          ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                          : 'bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50'
                      )}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick send button when guided selections are made */}
            {canSendGuided && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300 flex items-center gap-2">
                <p className="text-xs text-muted-foreground flex-1">
                  {isZh 
                    ? '可以直接发送，或在下方补充更多细节' 
                    : 'Send now, or add more details below'}
                </p>
                <Button
                  onClick={handleQuickSend}
                  size="sm"
                  disabled={isLoading}
                  className="bg-brand-500 hover:bg-brand-600 text-white rounded-full text-xs px-4"
                >
                  {isZh ? '开始分析' : 'Analyze'}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Input area */}
        <div className="p-4">
          {/* Show selected tags above input when guided */}
          {showGuidedInput && hasGuidedSelection && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedConcern && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-100 text-brand-700">
                  {concernChips.find(c => c.value === selectedConcern)?.label}
                  <button onClick={() => { setSelectedConcern(null); setSelectedDuration(null); setGuidedStep('concern'); }} className="hover:text-brand-900">✕</button>
                </span>
              )}
              {selectedDuration && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-100 text-brand-700">
                  {durationChips.find(d => d.value === selectedDuration)?.label}
                  <button onClick={() => { setSelectedDuration(null); setGuidedStep('duration'); }} className="hover:text-brand-900">✕</button>
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={inputValue + (interimTranscript ? interimTranscript : '')}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                showGuidedInput
                  ? (isZh ? '最近你心里在想什么？' : 'What’s been on your mind lately?')
                  : (isZh ? '继续描述你的感受...' : 'Tell me more about how you feel...')
              }
              disabled={isLoading}
              className="flex-1 rounded-full border-warm-200"
            />
            {speechSupported && (
              <Button
                onClick={handleMicToggle}
                size="icon"
                variant={isRecording ? 'destructive' : 'ghost'}
                className={cn(
                  'rounded-full shrink-0 h-9 w-9',
                  isRecording ? 'animate-pulse' : 'text-muted-foreground/50 hover:text-muted-foreground'
                )}
                disabled={isLoading}
              >
                {isRecording ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </Button>
            )}
            <Button
              onClick={handleSend}
              disabled={isLoading || (!inputValue.trim() && !canSendGuided)}
              size="icon"
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-full shrink-0"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>

          {/* Dynamic follow-up chips — stage-aware continuation of the dialogue */}
           {messages.length > 0 && !isLoading && !isCrisisState && quickReplyOptions.length === 0 && (
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-2">
                {isZh ? '继续对话' : 'Continue the conversation'}
              </p>
              <div className="flex flex-wrap gap-2">
                {followUpSuggestions.map((s) => {
                  // Deep-mode handoff: "Go deeper" routes through the bridge to /framework
                  const isDeepChip = s === 'Go deeper with analysis' || s === '深入分析';
                  // Other conversion-stage chips that should navigate instead of chat back.
                  const navTarget = (() => {
                    if (s === 'View practitioners' || s === '查看疗愈师') return '/booking-choice';
                    if (s === 'Book a session' || s === '预约一次咨询') return '/booking-choice';
                    return null;
                  })();
                  return (
                    <button
                      key={s}
                      onClick={() => {
                        if (isDeepChip) {
                          handleGoDeeper();
                        } else if (navTarget) {
                          fastNavigate(navTarget);
                        } else {
                          sendMessage(s);
                        }
                      }}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-full transition-colors',
                        isDeepChip
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : 'bg-brand-500 text-white hover:bg-brand-600'
                      )}
                    >
                      {isDeepChip && <Sparkles className="inline h-3 w-3 mr-1" />}
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Collapse toggle */}
          {messages.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center gap-1 w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {isExpanded
                ? (isZh ? '收起对话' : 'Collapse')
                : (isZh ? '展开对话' : 'Expand conversation')}
            </button>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground text-center pb-3 px-4">
          {isZh
            ? '仅供参考，不构成医疗或专业建议'
            : 'For informational use only; not intended as medical, therapeutic, or professional advice.'}
        </p>
      </div>
    </div>
  );
};

export default WellnessConcierge;
